import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Barcode, BookOpen, Boxes, FileUp, Package, PackageX, Pencil, Plus, Search, SlidersHorizontal, Trash2, Wrench, X,
} from 'lucide-react'
import {
  formatMoney, formatNumber, PERMISSIONS, PRODUCT_TYPES, stockStatus,
  thumbUrl, type ProductFilter, type ProductListItem, type ProductType,
} from '@najah/core'
import {
  Badge, Button, ConfirmDialog, EmptyState, Input, Pagination, Select, Spinner,
  Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { categoriesApi, flattenCategories, productsApi } from '../lib/catalogApi'
import { useAuth } from '../lib/auth'
import { ProductFormDialog } from '../components/ProductFormDialog'
import { StockAdjustDialog } from '../components/StockAdjustDialog'
import { ImportDialog } from '../components/ImportDialog'

const TYPE_ICONS: Record<ProductType, typeof Package> = {
  BOOK: BookOpen,
  STATIONERY: Package,
  SERVICE: Wrench,
  OTHER: Boxes,
}

function StockBadge({ product }: { product: ProductListItem }) {
  if (!product.trackStock) return <span className="text-xs text-fg-subtle">—</span>

  const status = stockStatus(product.quantity, product.minStock)
  const tone = status === 'out' ? 'danger' : status === 'low' ? 'warning' : 'success'
  const label = status === 'out' ? 'نفد' : status === 'low' ? 'على وشك' : 'متوفّر'

  return (
    <div className="flex items-center justify-center gap-2">
      <span className="font-semibold tabular-nums text-fg" dir="ltr">{formatNumber(product.quantity)}</span>
      <Badge tone={tone} dot>{label}</Badge>
    </div>
  )
}

export function ProductsPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.PRODUCT_MANAGE)
  const canAdjust = can(PERMISSIONS.STOCK_ADJUST)

  const [filter, setFilter] = useState<Partial<ProductFilter>>({
    page: 1, pageSize: 25, stock: 'all', isActive: 'all', sortBy: 'createdAt', sortDir: 'desc',
  })
  const [searchInput, setSearchInput] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [adjusting, setAdjusting] = useState<ProductListItem | null>(null)
  const [deleting, setDeleting] = useState<ProductListItem | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['products', filter],
    queryFn: () => productsApi.list(filter),
    placeholderData: keepPreviousData,
  })

  const { data: categoryTree } = useQuery({ queryKey: ['categories', 'tree'], queryFn: () => categoriesApi.tree(true) })
  const categoryOptions = useMemo(() => (categoryTree ? flattenCategories(categoryTree) : []), [categoryTree])

  // نجلب المنتج كاملاً عند التعديل — القائمة لا تحمل تفاصيل الكتاب
  const { data: editing, isPending: editingLoading } = useQuery({
    queryKey: ['products', 'detail', editingId],
    queryFn: () => productsApi.get(editingId!),
    enabled: !!editingId,
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      toast.success('تم حذف المنتج', 'يبقى في السجلات التاريخية')
      setDeleting(null)
    },
    onError: () => toast.error('تعذّر حذف المنتج'),
  })

  function patch(next: Partial<ProductFilter>) {
    setFilter((f) => ({ ...f, ...next, page: next.page ?? 1 }))
  }

  function toggleSort(column: NonNullable<ProductFilter['sortBy']>) {
    patch({
      sortBy: column,
      sortDir: filter.sortBy === column && filter.sortDir === 'desc' ? 'asc' : 'desc',
    })
  }

  const sortDirOf = (col: string) => (filter.sortBy === col ? (filter.sortDir ?? 'desc') : null)
  const activeFilters = [filter.categoryId, filter.type, filter.stock !== 'all' ? filter.stock : null,
    filter.isActive !== 'all' ? filter.isActive : null].filter(Boolean).length

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">المنتجات</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} منتج` : 'جارٍ التحميل…'}
          </p>
        </div>

        {canManage && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              startIcon={<FileUp className="size-4" />}
              onClick={() => setImportOpen(true)}
            >
              استيراد
            </Button>
            <Button startIcon={<Plus className="size-4" />} onClick={() => { setEditingId(null); setFormOpen(true) }}>
              إضافة منتج
            </Button>
          </div>
        )}
      </div>

      {/* ── البحث والترشيح ── */}
      <div className="space-y-3">
        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); patch({ search: searchInput.trim() || undefined }) }}
        >
          <Input
            placeholder="ابحث بالاسم أو الرمز أو الباركود أو ISBN…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            startIcon={<Search className="size-4" />}
            endIcon={
              searchInput ? (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); patch({ search: undefined }) }}
                  className="pointer-events-auto rounded p-0.5 text-fg-subtle hover:text-fg"
                  aria-label="مسح البحث"
                >
                  <X className="size-4" />
                </button>
              ) : undefined
            }
          />
          <Button type="submit" variant="secondary">بحث</Button>
          <Button
            type="button"
            variant={activeFilters ? 'primary' : 'secondary'}
            onClick={() => setShowFilters((v) => !v)}
            startIcon={<SlidersHorizontal className="size-4" />}
          >
            ترشيح{activeFilters ? ` (${activeFilters})` : ''}
          </Button>
        </form>

        {showFilters && (
          <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-2 lg:grid-cols-4">
            <Select
              label="التصنيف" value={filter.categoryId ?? ''}
              onChange={(e) => patch({ categoryId: e.target.value || undefined })}
              options={categoryOptions} placeholder="كل التصنيفات"
            />
            <Select
              label="النوع" value={filter.type ?? ''}
              onChange={(e) => patch({ type: (e.target.value || undefined) as ProductType | undefined })}
              options={Object.entries(PRODUCT_TYPES).map(([value, label]) => ({ value, label }))}
              placeholder="كل الأنواع"
            />
            <Select
              label="حالة المخزون" value={filter.stock ?? 'all'}
              onChange={(e) => patch({ stock: e.target.value as ProductFilter['stock'] })}
              options={[
                { value: 'all', label: 'الكل' },
                { value: 'in', label: 'متوفّر' },
                { value: 'low', label: 'على وشك النفاد' },
                { value: 'out', label: 'نفد' },
              ]}
            />
            <Select
              label="الحالة" value={filter.isActive ?? 'all'}
              onChange={(e) => patch({ isActive: e.target.value as ProductFilter['isActive'] })}
              options={[
                { value: 'all', label: 'الكل' },
                { value: 'yes', label: 'نشط' },
                { value: 'no', label: 'غير نشط' },
              ]}
            />
          </div>
        )}
      </div>

      {/* ── الجدول ── */}
      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th sortable sortDir={sortDirOf('nameAr')} onSort={() => toggleSort('nameAr')}>المنتج</Th>
              <Th sortable sortDir={sortDirOf('sku')} onSort={() => toggleSort('sku')}>الرمز</Th>
              <Th>التصنيف</Th>
              <Th align="center" sortable sortDir={sortDirOf('quantity')} onSort={() => toggleSort('quantity')}>
                المخزون
              </Th>
              <Th align="end" sortable sortDir={sortDirOf('retailPrice')} onSort={() => toggleSort('retailPrice')}>
                سعر البيع
              </Th>
              <Th align="end">التكلفة</Th>
              <Th align="center">إجراءات</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={8} cols={7} />
          ) : (
            <Tbody>
              {data?.items.map((p) => {
                const TypeIcon = TYPE_ICONS[p.type]
                return (
                  <Tr key={p.id} className={cn(!p.isActive && 'opacity-55')}>
                    <Td>
                      <div className="flex items-center gap-3">
                        {p.images?.[0] ? (
                          <img
                            src={thumbUrl(p.images[0]) ?? p.images[0]}
                            alt=""
                            loading="lazy"
                            className="size-9 shrink-0 rounded-lg object-cover"
                          />
                        ) : (
                          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-bg-subtle text-fg-subtle">
                            <TypeIcon className="size-4" />
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-fg">{p.nameAr}</p>
                          <p className="flex items-center gap-2 text-xs text-fg-subtle">
                            {PRODUCT_TYPES[p.type]}
                            {p.barcode && (
                              <span className="inline-flex items-center gap-1" dir="ltr">
                                <Barcode className="size-3" />{p.barcode}
                              </span>
                            )}
                            {!p.isActive && <Badge tone="neutral" className="text-[10px]">غير نشط</Badge>}
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td className="font-mono text-xs text-fg-muted" dir="ltr">{p.sku}</Td>
                    <Td className="text-fg-muted">{p.categoryName ?? '—'}</Td>
                    <Td align="center"><StockBadge product={p} /></Td>
                    <Td align="end" className="font-semibold tabular-nums" dir="ltr">{formatMoney(p.retailPrice)}</Td>
                    <Td align="end" className="tabular-nums text-fg-muted" dir="ltr">{formatMoney(p.costPrice)}</Td>
                    <Td align="center">
                      <div className="flex items-center justify-center gap-1">
                        {canAdjust && p.trackStock && (
                          <Button
                            variant="ghost" size="icon" title="تسوية المخزون"
                            onClick={() => setAdjusting(p)}
                          >
                            <Boxes className="size-4" />
                          </Button>
                        )}
                        {canManage && (
                          <>
                            <Button
                              variant="ghost" size="icon" title="تعديل"
                              onClick={() => { setEditingId(p.id); setFormOpen(true) }}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon" title="حذف"
                              className="text-danger hover:bg-danger-subtle"
                              onClick={() => setDeleting(p)}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<PackageX className="size-10" />}
            title={filter.search || activeFilters ? 'لا توجد نتائج مطابقة' : 'لا توجد منتجات بعد'}
            description={
              filter.search || activeFilters
                ? 'جرّب تعديل البحث أو إزالة المرشّحات'
                : 'ابدأ بإضافة أول منتج إلى المكتبة'
            }
            action={
              filter.search || activeFilters ? (
                <Button variant="secondary" onClick={() => { setSearchInput(''); setFilter({ page: 1, pageSize: 25, stock: 'all', isActive: 'all', sortBy: 'createdAt', sortDir: 'desc' }) }}>
                  مسح الترشيح
                </Button>
              ) : canManage ? (
                <Button startIcon={<Plus className="size-4" />} onClick={() => { setEditingId(null); setFormOpen(true) }}>
                  إضافة منتج
                </Button>
              ) : undefined
            }
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={(page) => setFilter((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => patch({ pageSize })}
          />
        )}
      </TableWrapper>

      {/* ── النوافذ ── */}
      {formOpen && editingId && editingLoading ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-[var(--overlay)]">
          <Spinner className="size-7 text-white" />
        </div>
      ) : (
        <ProductFormDialog
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditingId(null) }}
          product={editingId ? editing : null}
        />
      )}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />

      <StockAdjustDialog open={!!adjusting} onClose={() => setAdjusting(null)} product={adjusting} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && removeMutation.mutate(deleting.id)}
        loading={removeMutation.isPending}
        title="حذف المنتج"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg">{deleting?.nameAr}</span> من القوائم.
            <br />
            حركات المخزون والفواتير السابقة تبقى محفوظة — الحذف ناعم ولا يفقد أي سجل تاريخي.
          </>
        }
      />
    </div>
  )
}
